import { useEffect } from "react";

/**
 * Reusable SEO component for per-page meta injection.
 * Updates <title>, meta description, canonical, Open Graph, Twitter tags,
 * and (optionally) FAQPage / SoftwareApplication structured data.
 *
 * Renders nothing — performs DOM side-effects on mount + when props change.
 */
function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, name] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, payload) {
  // Remove existing tag with this id
  const prev = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (prev) prev.remove();
  if (!payload) return;
  const tag = document.createElement("script");
  tag.type = "application/ld+json";
  tag.setAttribute("data-seo-id", id);
  tag.textContent = JSON.stringify(payload);
  document.head.appendChild(tag);
}

export default function SEO({
  title,
  description,
  canonical,
  ogImage = "https://creaiops.com/og-image.png",
  faq,
  softwareApp = true,
}) {
  useEffect(() => {
    if (title) document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="robots"]', "content", "index,follow,max-snippet:-1,max-image-preview:large");

    // Open Graph
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:site_name"]', "content", "CREAIOps");
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:image:alt"]', "content", title);
    setMeta('meta[property="og:locale"]', "content", "en_US");

    // Twitter
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:site"]', "content", "@creaiops");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", ogImage);

    setCanonical(canonical);

    // Structured data — FAQ
    if (faq && faq.length) {
      setJsonLd("faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((q) => ({
          "@type": "Question",
          name: q.q,
          acceptedAnswer: { "@type": "Answer", text: q.a },
        })),
      });
    } else {
      setJsonLd("faq", null);
    }

    // Structured data — SoftwareApplication (per-page)
    if (softwareApp) {
      setJsonLd("software-app", {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "CREAIOps",
        applicationCategory: "BusinessApplication",
        applicationSubCategory: "AIOps / Incident Management",
        operatingSystem: "Web",
        url: canonical || "https://creaiops.com/",
        description,
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
      });
    }

    return () => {
      // Reset structured data on unmount so other pages can re-set
      setJsonLd("faq", null);
      setJsonLd("software-app", null);
    };
  }, [title, description, canonical, ogImage, faq, softwareApp]);

  return null;
}
